/**
 * CTRL_13 Plugin - Data Provenance Tracking
 * Custom UI with data lineage visualization and source tracking
 * Extends GenericConfigPanel with provenance graph and audit trail
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Alert,
  Table,
  Tag,
  Space,
  Timeline,
  Tree,
} from 'antd';
import {
  ApartmentOutlined,
  DatabaseOutlined,
  BranchesOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import GenericConfigPanel from '../GenericConfigPanel';
import { colors } from '../../../../theme/designTokens';

/**
 * Data Provenance Dashboard Component
 */
function DataProvenanceDashboard({ config, projectId }) {
  const [stats, setStats] = useState({
    trackedSources: 0,
    transformations: 0,
    completeLineage: 0,
  });
  const [sources, setSources] = useState([]);
  const [lineage, setLineage] = useState([]);

  useEffect(() => {
    if (config?.enabled && projectId) {
      fetchProvenanceData();
    }
  }, [config, projectId]);

  const fetchProvenanceData = async () => {
    // Mock data - replace with actual API call
    setTimeout(() => {
      setStats({
        trackedSources: 15,
        transformations: 48,
        completeLineage: 98.5,
      });
      setSources([
        {
          id: 1,
          name: 'Customer Database',
          type: 'PostgreSQL',
          records: 10420,
          lastSync: new Date().toISOString(),
        },
        {
          id: 2,
          name: 'External API',
          type: 'REST API',
          records: 5200,
          lastSync: new Date(Date.now() - 3600000).toISOString(),
        },
      ]);
      setLineage([
        {
          key: '1',
          title: 'Raw Data Collection',
          children: [
            {
              key: '1-1',
              title: 'Customer Database (10,420 records)',
            },
            {
              key: '1-2',
              title: 'External API (5,200 records)',
            },
          ],
        },
        {
          key: '2',
          title: 'Data Transformation',
          children: [
            {
              key: '2-1',
              title: 'Cleaning & Validation',
            },
            {
              key: '2-2',
              title: 'Feature Engineering',
            },
          ],
        },
        {
          key: '3',
          title: 'Training Dataset (15,620 records)',
        },
      ]);
    }, 500);
  };

  const columns = [
    {
      title: 'Source',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <Space>
          <DatabaseOutlined />
          {name}
          <Tag>{record.type}</Tag>
        </Space>
      ),
    },
    {
      title: 'Records',
      dataIndex: 'records',
      key: 'records',
      align: 'right',
    },
    {
      title: 'Last Sync',
      dataIndex: 'lastSync',
      key: 'lastSync',
      render: (ts) => new Date(ts).toLocaleString(),
    },
  ];

  if (!config?.enabled) {
    return (
      <Alert
        message="Data Provenance Tracking Disabled"
        description="Enable to track data lineage and maintain complete audit trails."
        type="info"
        showIcon
      />
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Provenance Statistics */}
      <Row gutter={16}>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="Tracked Sources"
              value={stats.trackedSources}
              prefix={<DatabaseOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="Transformations"
              value={stats.transformations}
              prefix={<BranchesOutlined />}
              valueStyle={{ color: colors.primary[500] }}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="Complete Lineage"
              value={stats.completeLineage}
              suffix="%"
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: colors.status.pass }}
            />
          </Card>
        </Col>
      </Row>

      {/* Data Sources */}
      <div>
        <h4>Active Data Sources</h4>
        <Table
          columns={columns}
          dataSource={sources}
          rowKey="id"
          size="small"
          pagination={false}
        />
      </div>

      {/* Data Lineage Tree */}
      <Card size="small" title={<Space><ApartmentOutlined /> Data Lineage</Space>}>
        <Tree
          showLine
          defaultExpandAll
          treeData={lineage}
        />
      </Card>
    </Space>
  );
}

/**
 * CTRL_13 Plugin Component
 */
function CTRL_13_Plugin({ projectId, controlCode }) {
  const [config, setConfig] = useState({
    enabled: false,
    track_transformations: true,
  });

  return (
    <div>
      {/* Provenance Dashboard */}
      <DataProvenanceDashboard config={config} projectId={projectId} />

      {/* Generic Config Panel - usePlugin={false} prevents infinite loop */}
      <GenericConfigPanel projectId={projectId} controlCode={controlCode} usePlugin={false} />
    </div>
  );
}

export default CTRL_13_Plugin;
